import React, { useState } from 'react';
import { X, Mail, Smartphone, Key, Check, AlertCircle } from 'lucide-react';

const PasswordRecoveryModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState('method'); // method, verify, reset
  const [recoveryMethod, setRecoveryMethod] = useState('email');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  if (!isOpen) return null;

  const handleMethodSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError('');

    // Simulate API call
    setTimeout(() => {
      setLoading(false);
      setStep('verify');
      setSuccess(`Verification code sent to your ${recoveryMethod}`);
    }, 1500);
  };

  const handleVerificationSubmit = async (e) => {
    e?.preventDefault();
    if (verificationCode !== '123456') {
      setError('Invalid verification code. Please try again.');
      return;
    }
    setError('');
    setStep('reset');
  };

  const handlePasswordReset = async (e) => {
    e?.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (newPassword?.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);
    setError('');

    // Simulate API call
    setTimeout(() => {
      setLoading(false);
      setSuccess('Password reset successfully! You can now sign in with your new password.');
      setTimeout(() => {
        onClose();
      }, 2000);
    }, 1500);
  };

  const resetModal = () => {
    setStep('method');
    setRecoveryMethod('email');
    setEmail('');
    setPhone('');
    setVerificationCode('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    setSuccess('');
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Password Recovery</h2>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Step 1: Choose Recovery Method */}
          {step === 'method' && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="p-3 bg-blue-100 rounded-full">
                    <Key className="h-8 w-8 text-blue-600" />
                  </div>
                </div>
                <p className="text-gray-600">Choose how you would like to recover your password</p>
              </div>

              <form onSubmit={handleMethodSubmit} className="space-y-4">
                {/* Recovery Method Selection */}
                <div className="space-y-3">
                  <div
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                      recoveryMethod === 'email' ?'border-blue-500 bg-blue-50' :'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setRecoveryMethod('email')}
                  >
                    <div className="flex items-center space-x-3">
                      <input
                        type="radio"
                        name="recovery"
                        value="email"
                        checked={recoveryMethod === 'email'}
                        onChange={(e) => setRecoveryMethod(e?.target?.value)}
                        className="h-4 w-4 text-blue-600"
                      />
                      <Mail className="h-5 w-5 text-gray-500" />
                      <div>
                        <div className="font-medium text-gray-900">Email Verification</div>
                        <div className="text-sm text-gray-500">Receive a reset link via email</div>
                      </div>
                    </div>
                  </div>

                  <div
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                      recoveryMethod === 'sms' ?'border-blue-500 bg-blue-50' :'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setRecoveryMethod('sms')}
                  >
                    <div className="flex items-center space-x-3">
                      <input
                        type="radio"
                        name="recovery"
                        value="sms"
                        checked={recoveryMethod === 'sms'}
                        onChange={(e) => setRecoveryMethod(e?.target?.value)}
                        className="h-4 w-4 text-blue-600"
                      />
                      <Smartphone className="h-5 w-5 text-gray-500" />
                      <div>
                        <div className="font-medium text-gray-900">SMS Verification</div>
                        <div className="text-sm text-gray-500">Receive a code via text message</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Email Input */}
                {recoveryMethod === 'email' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e?.target?.value)}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter your email address"
                      required
                    />
                  </div>
                )}

                {/* Phone Input */}
                {recoveryMethod === 'sms' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e?.target?.value)}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter your phone number"
                      required
                    />
                  </div>
                )}

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <div className="flex items-center space-x-2">
                      <AlertCircle className="h-4 w-4 text-red-500" />
                      <span className="text-sm text-red-700">{error}</span>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || (recoveryMethod === 'email' && !email) || (recoveryMethod === 'sms' && !phone)}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                      Sending...
                    </>
                  ) : (
                    `Send Verification Code`
                  )}
                </button>
              </form>
            </div>
          )}

          {/* Step 2: Verify Code */}
          {step === 'verify' && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="p-3 bg-green-100 rounded-full">
                    <Mail className="h-8 w-8 text-green-600" />
                  </div>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Check Your {recoveryMethod === 'email' ? 'Email' : 'Phone'}</h3>
                <p className="text-gray-600">
                  We sent a verification code to {recoveryMethod === 'email' ? email : phone}
                </p>
              </div>

              {success && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center space-x-2">
                    <Check className="h-4 w-4 text-green-500" />
                    <span className="text-sm text-green-700">{success}</span>
                  </div>
                </div>
              )}

              <form onSubmit={handleVerificationSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Verification Code
                  </label>
                  <input
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e?.target?.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-center text-lg font-mono tracking-widest focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="000000"
                    maxLength="6"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Demo code: 123456</p>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <div className="flex items-center space-x-2">
                      <AlertCircle className="h-4 w-4 text-red-500" />
                      <span className="text-sm text-red-700">{error}</span>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={!verificationCode || verificationCode?.length !== 6}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Verify Code
                </button>

                <button
                  type="button"
                  onClick={() => setStep('method')}
                  className="w-full text-center text-sm text-gray-600 hover:text-gray-800"
                >
                  Back to recovery method
                </button>
              </form>
            </div>
          )}

          {/* Step 3: Reset Password */}
          {step === 'reset' && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="p-3 bg-purple-100 rounded-full">
                    <Key className="h-8 w-8 text-purple-600" />
                  </div>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Set New Password</h3>
                <p className="text-gray-600">
                  Choose a strong password for your account
                </p>
              </div>

              <form onSubmit={handlePasswordReset} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Password
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e?.target?.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter new password"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Password must be at least 8 characters long
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e?.target?.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Confirm new password"
                    required
                  />
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <div className="flex items-center space-x-2">
                      <AlertCircle className="h-4 w-4 text-red-500" />
                      <span className="text-sm text-red-700">{error}</span>
                    </div>
                  </div>
                )}

                {success && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="flex items-center space-x-2">
                      <Check className="h-4 w-4 text-green-500" />
                      <span className="text-sm text-green-700">{success}</span>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || !newPassword || !confirmPassword || newPassword !== confirmPassword}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                      Resetting Password...
                    </>
                  ) : (
                    'Reset Password'
                  )}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PasswordRecoveryModal;